/**
 * Measurement definitions from /config/categories (meas_names text[] in DB).
 */
import type { CategoryDef } from './config';

export type MeasValueType = 'length' | 'perimeter';

export interface CategoryMeasDef {
  meas_name: string;
  display_name: string;
  value_type: MeasValueType;
}

function formatMeasKey(measName: string): string {
  return measName.replace(/^MEAS__/, '').replace(/_/g, ' ');
}

function inferValueType(measName: string): MeasValueType {
  if (measName === 'MEAS__HC' || measName === 'MEAS__AC') return 'perimeter';
  return 'length';
}

export function getCategoryMeasNames(category: CategoryDef): string[] {
  if (Array.isArray(category.meas_names) && category.meas_names.length) {
    return category.meas_names.filter(Boolean);
  }
  return [];
}

export function getCategoryMeasDefs(category: CategoryDef): CategoryMeasDef[] {
  const names = getCategoryMeasNames(category);
  return names.map((meas_name, i) => ({
    meas_name,
    display_name:
      category.meas_display_names?.[i]?.trim() || formatMeasKey(meas_name),
    value_type:
      (category.meas_value_types?.[i] as MeasValueType | undefined) ??
      inferValueType(meas_name),
  }));
}

export function buildMeasLabelToCategoryId(
  categories: CategoryDef[]
): Record<string, number> {
  const out: Record<string, number> = {};
  categories.forEach((c) => {
    getCategoryMeasNames(c).forEach((meas) => {
      out[meas] = c.id;
    });
  });
  return out;
}

export function buildCategoryIdToMeasNames(
  categories: CategoryDef[]
): Record<number, string[]> {
  const out: Record<number, string[]> = {};
  categories.forEach((c) => {
    const names = getCategoryMeasNames(c);
    if (names.length) out[c.id] = names;
  });
  return out;
}

/** Primary meas (first in meas_names) — backward compat with meas_name. */
export function buildCategoryIdToMeasLabel(
  categories: CategoryDef[]
): Record<number, string> {
  const out: Record<number, string> = {};
  categories.forEach((c) => {
    const names = getCategoryMeasNames(c);
    if (names[0]) out[c.id] = names[0];
  });
  return out;
}

export function buildMeasDisplayNameMap(
  categories: CategoryDef[]
): Record<string, string> {
  const out: Record<string, string> = {};
  categories.forEach((c) => {
    getCategoryMeasDefs(c).forEach((d) => {
      out[d.meas_name] = d.display_name;
    });
  });
  return out;
}

export function buildPerimeterMeasLabels(
  categories: CategoryDef[]
): Set<string> {
  const out = new Set<string>();
  categories.forEach((c) => {
    getCategoryMeasDefs(c).forEach((d) => {
      if (d.value_type === 'perimeter') out.add(d.meas_name);
    });
  });
  return out;
}

export function getLengthMeasForCategory(
  category: CategoryDef | undefined
): string | null {
  if (!category) return null;
  const defs = getCategoryMeasDefs(category);
  return defs.find((d) => d.value_type === 'length')?.meas_name ?? null;
}

export function getPerimeterMeasForCategory(
  category: CategoryDef | undefined
): string | null {
  if (!category) return null;
  const defs = getCategoryMeasDefs(category);
  return defs.find((d) => d.value_type === 'perimeter')?.meas_name ?? null;
}

export function categoryHasPairedLengthAndPerimeter(
  category: CategoryDef | undefined
): boolean {
  if (!category) return false;
  const defs = getCategoryMeasDefs(category);
  return (
    defs.some((d) => d.value_type === 'length') &&
    defs.some((d) => d.value_type === 'perimeter')
  );
}

function normalizeCategoryCode(code: string): string {
  return (code ?? '').toLowerCase().trim().replace(/\s+/g, '_');
}

/** Resolve a structure category by its backend code (e.g. fetal_brain). */
export function findCategoryByCode(
  categories: CategoryDef[],
  code: string
): CategoryDef | undefined {
  const key = normalizeCategoryCode(code);
  if (!key) return undefined;
  return categories.find((c) => normalizeCategoryCode(c.code) === key);
}

/** Category that owns the BPD (length) + HC (perimeter) pair — from /config/categories. */
export function findHcEllipseCategory(
  categories: CategoryDef[]
): CategoryDef | undefined {
  const fetalBrain = findCategoryByCode(categories, 'fetal_brain');
  if (fetalBrain && categoryHasPairedLengthAndPerimeter(fetalBrain)) {
    return fetalBrain;
  }
  return categories.find((c) => categoryHasPairedLengthAndPerimeter(c));
}

/** All category ids that have a length + perimeter meas pair (e.g. BPD + HC). */
export function getPairedBiometryCategoryIds(categories: CategoryDef[]): Set<number> {
  const ids = new Set<number>();
  categories.forEach((c) => {
    if (categoryHasPairedLengthAndPerimeter(c)) ids.add(c.id);
  });
  return ids;
}

/** max_structures from /config/categories (default 1). */
export function getMaxStructuresFromApi(
  category: CategoryDef | undefined
): number {
  if (!category) return 1;
  const n = category.max_structures;
  if (n == null || n < 1) return 1;
  return n;
}

/** Save/dedup: paired BPD+HC categories allow 2 biometry slots per frame. */
export function getEffectiveMaxStructuresForSave(
  category: CategoryDef | undefined
): number {
  if (!category) return 1;
  if (categoryHasPairedLengthAndPerimeter(category)) return 2;
  return getMaxStructuresFromApi(category);
}

/** Drawing-tool limit: structure outlines only (not MEAS__ rows or derived BPD lines). */
export function isStructureShapeForCategoryLimit(shape: {
  categoryId?: number | null;
  segmentType?: string;
  backendLabel?: string | null;
  derivedFromShapeId?: string;
}): boolean {
  if (shape.categoryId == null) return false;
  if (shape.derivedFromShapeId) return false;
  if (shape.segmentType === 'measurement') return false;
  const label = String(shape.backendLabel ?? '').trim();
  if (label.startsWith('MEAS__')) return false;
  return true;
}

export function countStructureShapesForCategory(
  shapes: Array<{
    categoryId?: number | null;
    segmentType?: string;
    backendLabel?: string | null;
    derivedFromShapeId?: string;
  }>,
  categoryId: number
): number {
  return shapes.filter(
    (s) => s.categoryId === categoryId && isStructureShapeForCategoryLimit(s)
  ).length;
}

function presetMeasKey(label: unknown): string {
  return String(label ?? '')
    .trim()
    .split(/\s+/)[0];
}

/**
 * Preset menu + replace modal: includes biometry ellipses (MEAS__AC, MEAS__HC)
 * that are the actual Abdomen / Head Circumference drawing, not only structure rows.
 */
export function isPresetDrawnShapeForCategory(
  shape: {
    categoryId?: number | null;
    segmentType?: string;
    backendLabel?: string | null;
    derivedFromShapeId?: string;
    type?: string;
  },
  categoryId: number,
  categories: CategoryDef[] = []
): boolean {
  if (shape.categoryId !== categoryId) return false;
  if (shape.derivedFromShapeId) return false;

  const cat = categories.find((c) => c.id === categoryId);
  const perimeterMeas = cat ? getPerimeterMeasForCategory(cat) : null;
  const lengthMeas = cat ? getLengthMeasForCategory(cat) : null;
  const meas = presetMeasKey(shape.backendLabel);

  if (
    (shape.type === 'line' || shape.type === 'ruler') &&
    lengthMeas &&
    meas === lengthMeas
  ) {
    return false;
  }

  if (isStructureShapeForCategoryLimit(shape)) return true;

  if (
    shape.type === 'circle' &&
    perimeterMeas &&
    (meas === perimeterMeas || shape.segmentType === 'measurement')
  ) {
    return true;
  }

  return false;
}

export function countPresetDrawnShapesForCategory(
  shapes: Array<{
    categoryId?: number | null;
    segmentType?: string;
    backendLabel?: string | null;
    derivedFromShapeId?: string;
    type?: string;
    isManual?: boolean;
  }>,
  categoryId: number,
  categories: CategoryDef[] = []
): number {
  return shapes.filter((s) =>
    isPresetDrawnShapeForCategory(s, categoryId, categories)
  ).length;
}

export function buildViewMeasurementKeys(
  categories: CategoryDef[],
  qualityRuleCategoryIds: Iterable<number> | null | undefined
): Set<string> {
  const idToMeas = buildCategoryIdToMeasNames(categories);
  const keys = new Set<string>();
  if (!qualityRuleCategoryIds) return keys;
  const ids = Array.isArray(qualityRuleCategoryIds)
    ? qualityRuleCategoryIds
    : Array.from(qualityRuleCategoryIds);
  for (const catId of ids) {
    (idToMeas[catId] ?? []).forEach((m) => keys.add(m));
  }
  return keys;
}

export function buildViewMeasurementKeysFromViewDef(
  categories: CategoryDef[],
  viewDef: { quality_rules?: { category_id: number }[] } | null | undefined
): Set<string> {
  if (!viewDef?.quality_rules?.length) return new Set();
  return buildViewMeasurementKeys(
    categories,
    viewDef.quality_rules.map((r) => r.category_id)
  );
}

export type MeasGeometryContext = {
  perimeterMeasLabels: Set<string>;
  lengthToPerimeter: Record<string, string>;
  perimeterToLength: Record<string, string>;
};

export function buildMeasGeometryContext(
  categories: CategoryDef[]
): MeasGeometryContext {
  const perimeterMeasLabels = buildPerimeterMeasLabels(categories);
  const lengthToPerimeter: Record<string, string> = {};
  categories.forEach((c) => {
    const length = getLengthMeasForCategory(c);
    const perimeter = getPerimeterMeasForCategory(c);
    if (length && perimeter) lengthToPerimeter[length] = perimeter;
  });
  const perimeterToLength = Object.fromEntries(
    Object.entries(lengthToPerimeter).map(([length, perimeter]) => [
      perimeter,
      length,
    ])
  );
  return { perimeterMeasLabels, lengthToPerimeter, perimeterToLength };
}

export function inferDefaultMeasForViewDraw(
  viewMeasKeys: Iterable<string> | undefined,
  shapeType: string | undefined,
  perimeterMeasLabels: Set<string>
): string | null {
  const keys = viewMeasKeys ? new Set(viewMeasKeys) : new Set<string>();
  const t = (shapeType ?? '').toLowerCase();
  if (t === 'line' || t === 'ruler') {
    for (const k of keys) {
      if (!perimeterMeasLabels.has(k)) return k;
    }
  }
  if (t === 'circle') {
    for (const k of keys) {
      if (perimeterMeasLabels.has(k)) return k;
    }
  }
  return null;
}
